/**
 * PR-F P1 — Dimension-side mesh + text-renderable factories.
 *
 * The dimension adapter (`dimensionToPrimitives`) emits a primitive
 * list under `geometry.primitives`. Line-class primitives (extension-
 * line, dim-line, leader, arc) flow through the BatchedLayer thick-line
 * pipeline via `geometryToLineVertices`. The two remaining primitive
 * kinds — `arrow` / `tick` heads, and `text` — need their own
 * Three.js objects:
 *
 *   * `buildDimensionArrowMeshes` — one `THREE.InstancedMesh` per
 *     `(variant, colorHex)` group. Each instance carries a
 *     `translate(tip) · rotateZ(direction) · scale(size)` matrix so
 *     the unit-sized arrow prototype renders at the correct pose.
 *
 *   * `synthesizeDimensionTextRenderables` — one synthetic
 *     `RenderableEntity` per `text` primitive, with
 *     `geometry.kind = "text"`. Synthesised renderables are pushed
 *     through the same SDF / SHX text pipeline real Text entities use.
 *     Synthetic ids are namespaced with `::text::<idx>` so dispose
 *     can track them per-source-dim without colliding with real Text
 *     ids.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1
 *
 * Arrow prototype reuse and dim-line direction → rotation match the
 * `arrowMarkerMesh` adapter and the SVG renderer 1:1. No fall-backs,
 * no quadratic substitutions, no simplified single-mesh path — full
 * `(variant, colorHex)` grouping is honoured even when both
 * arrowheads of a dim line share variant + color.
 */

import type { ArrowVariant, RenderableEntity } from "../types";
import { directMeshLayerStamp } from "../layerChainVisibility";
import {
  buildArrowInstanceMeshes,
  type ArrowDecoratorObject3D,
  type ArrowInstanceInput,
} from "../dimensions/arrowInstanceMesh";

/* ── Arrow / tick grouping ───────────────────────────────────────── */

/** Map `tick` variant tag onto the matching ArrowVariant prototype id. */
function tickVariantToArrow(variant: "arch-tick" | "oblique"): ArrowVariant {
  return variant;
}

/**
 * Walk a dimension renderable's primitives and emit one
 * `THREE.InstancedMesh` per `(variant, colorHex)` group.
 *
 * Filled variants (`closed-filled`, `closed`, `dot`, `arch-tick`,
 * `oblique`) use `MeshBasicMaterial` (rendered as triangle mesh).
 * Outline variants (`closed-blank`, `dot-blank`, `open`, `open30`)
 * use `LineBasicMaterial` and an `InstancedMesh` wrapped around a
 * line-segment BufferGeometry — Three.js does not have
 * `InstancedLineSegments` natively, so we materialise the line geometry
 * directly with the prototype's BufferGeometry and the `InstancedMesh`
 * matrix path still works (each instance's matrix transforms the
 * line-vertex pair).
 *
 * `none` variant prototypes are skipped (empty geometry — no draw).
 *
 * Returns an empty array if the renderable has no arrow / tick
 * primitives (e.g. DIMBLK=_NONE for both ends).
 */
/**
 * Output union for the factory — filled arrowheads materialise as a
 * single `InstancedMesh` per (variant, colorHex) group (triangle draw
 * path), outline arrowheads (DIMBLK `_OPEN`, `_OPEN30`, `_CLOSEDBLANK`,
 * `_DOTBLANK`) materialise as one `THREE.LineSegments` per instance
 * (gl.LINES draw path). Re-exported alias of the shared
 * {@link ArrowDecoratorObject3D} so existing dimension callers keep the
 * name.
 */
export type DimensionArrowObject3D = ArrowDecoratorObject3D;

export function buildDimensionArrowMeshes(
  renderable: RenderableEntity,
): DimensionArrowObject3D[] {
  if (renderable.geometry.kind !== "dimension") return [];

  // Map every `arrow` / `tick` primitive into the shared arrow-instance
  // shape (a `tick` resolves to its matching arrow prototype variant). The
  // shared builder groups by `(variant, colorHex)` and materialises the
  // filled-InstancedMesh / outline-LineSegments objects (jscpd one-truth).
  const instances: ArrowInstanceInput[] = [];
  for (const prim of renderable.geometry.primitives) {
    if (prim.kind === "arrow") {
      // The `arrow` primitive is a structural superset of `ArrowInstanceInput`
      // — push it directly.
      instances.push(prim);
    } else if (prim.kind === "tick") {
      // A `tick` resolves to its matching arrow prototype variant.
      instances.push({
        tip: prim.tip,
        direction: prim.direction,
        size: prim.size,
        variant: tickVariantToArrow(prim.variant),
        colorHex: prim.colorHex,
        // Codex PR #784 round 11 — a block-child tick carries the enclosing
        // INSERT's full 2x2 map (`glyphLinear`), which a unit direction plus a
        // scalar cannot encode. The `arrow` branch above pushes the primitive
        // straight through as a structural superset and so forwards it for
        // free; this branch rebuilds an explicit literal, so it has to forward
        // the field itself or a sheared / non-uniformly scaled tick would fall
        // back to the similarity pose.
        ...(prim.glyphLinear ? { glyphLinear: prim.glyphLinear } : {}),
      });
    }
  }
  // Codex S9b round-5 P2 ("Attribute block-child dimension arrows to the
  // parent") — a DIMENSION that is an MLEADER block-content child carries a
  // `renderParentId`; its own synthetic id is intentionally ABSENT from the
  // spatial index, so stamping the arrow decorators with it would make
  // `applySceneVisibilityMask` (keyed on spatial-index membership) hide the
  // arrows in model-space viewport passes even when the parent MLEADER is
  // visible. Stamp the PARENT id so the arrows survive the mask WITH the
  // parent — mirrors the hatch-fill / text-child factories. Falls back to the
  // real id for a standalone DIMENSION (`renderParentId` absent).
  return buildArrowInstanceMeshes(
    instances,
    renderable.renderParentId ?? renderable.id,
    "dimension-arrow",
    directMeshLayerStamp(renderable),
  );
}

/* ── Text synthesis ──────────────────────────────────────────────── */

/**
 * Conservative bbox half-extent factor for synthesised text. The SDF
 * pipeline computes per-glyph bbox itself; we just need a non-zero
 * bound so spatial-index / dispose pipelines don't treat the entry as
 * degenerate.
 */
const TEXT_BBOX_HALF_FACTOR = 0.3;

/**
 * Walk a dimension renderable's primitives, emit one synthetic
 * `RenderableEntity` per `text` primitive (DIMTXT / DIMTOL / Override
 * blob). The synthesised entity carries `geometry.kind = "text"` so
 * it flows through the existing SDF / SHX text pipeline (PR-E2 +
 * PR-E5) — no separate text renderer for dimensions.
 *
 * Id namespacing: `${renderable.id}::text::${i}`. The `::` separator
 * matches the convention used by `array-rect` composite keys, and
 * keeps synthesised ids distinct from any real entity id (which
 * never contain `::` per DXF naming rules).
 *
 * `type` is preserved as `"Dimension"` so downstream selection /
 * hit-test code knows the parent kind. The synthetic ids never
 * collide with real Text/MText ids.
 */
export function synthesizeDimensionTextRenderables(
  renderable: RenderableEntity,
): RenderableEntity[] {
  if (renderable.geometry.kind !== "dimension") return [];

  const out: RenderableEntity[] = [];
  let i = 0;
  for (const prim of renderable.geometry.primitives) {
    if (prim.kind !== "text") {
      continue;
    }
    const textPrim = prim;
    const halfX = Math.max(
      textPrim.content.length * textPrim.height * TEXT_BBOX_HALF_FACTOR,
      textPrim.height,
    );
    const halfY = textPrim.height;
    const synthetic: RenderableEntity = {
      id: `${renderable.id}::text::${i}`,
      // Codex S9b P2 — attribute the synthetic measurement text to the REAL
      // DIMENSION so the viewport-visibility mask + base layout pre-hide (both
      // keyed on spatial-index membership) treat it as the parent. The synthetic
      // `::text::i` id resolves in neither index (same fix as MLEADER labels).
      // Codex round-6 P2 ("Preserve the MLEADER parent for block-child labels"):
      // when the DIMENSION is itself an MLEADER block-content child it already
      // carries `renderParentId` = the owning MLEADER (a spatial-index entity);
      // overwriting it with the child dimension id (absent from the index) would
      // make the mask drop the measurement text even when the callout is
      // visible. Preserve the existing parent — mirrors the arrow-decorator fix.
      renderParentId: renderable.renderParentId ?? renderable.id,
      // Round 3 P1 — a DIMENSION that is itself a block child carries its
      // INSERT's layer chain; the synthesised label inherits it so the
      // parent-layer conjunction reaches the text too.
      ...(renderable.renderParentLayers
        ? { renderParentLayers: renderable.renderParentLayers }
        : {}),
      spaceId: renderable.spaceId,
      type: renderable.type,
      bounds: {
        minX: textPrim.position.x - halfX,
        minY: textPrim.position.y - halfY,
        maxX: textPrim.position.x + halfX,
        maxY: textPrim.position.y + halfY,
      },
      layer: renderable.layer,
      colorHex: textPrim.colorHex,
      lineweightWorld: renderable.lineweightWorld,
      // PR-LT3 — synthesised dimension text inherits the parent's linetype
      // (text buckets are continuous regardless, but keep the field faithful).
      linetype: renderable.linetype,
      zOrder: renderable.zOrder,
      geometry: {
        kind: "text",
        position: textPrim.position,
        rotation: textPrim.rotation,
        height: textPrim.height,
        widthFactor: 1,
        oblique: 0,
        styleName: textPrim.styleName,
        content: textPrim.content,
        alignment: textPrim.alignment,
        // GitHub Codex review PR #558 (post-rebase fresh pass) P1 —
        // handedness companion to `rotation` above, sourced from
        // `DimensionEntity.textMirrored`. Reuses the SAME `upsideDown` bit
        // real TEXT's own MIRRTEXT=1 full reflection sets — no new render
        // pipeline, just a new source for an existing sink
        // (`sceneTextMeshFactories.ts`'s `applyMirrorScale`).
        upsideDown: textPrim.mirrored,
      },
    };
    out.push(synthetic);
    i += 1;
  }
  return out;
}
